import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  useCurrentAccount,
  ConnectModal,
  useDisconnectWallet,
} from "@mysten/dapp-kit";
import { fetchWalletSuccess, removeWalletAddress } from "../redux/actions/wallet";
import ConnectWalletModal from "components/Modals/ConnectWalletModal";
import DisconnectModal from "components/Modals/DisconnectModal";
import ConnectionOptionModal from "components/Modals/ConnectionOptionModal";
import MigrationModal from "components/Modals/MigrationModal";
import { connectResponse } from "utils/connectResponse";
import * as icon from "services/icon";
import { WALLET_TYPE } from "src/constants/walletType";
import { WALLET } from "src/constants/routes";

const Migration = () => {
  // ICON Wallet
  const { walletAddress: address, walletType, walletPath, transport } = useSelector((state) => state.wallet);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // SUI Wallet
  const walletAccount = useCurrentAccount();
  const { mutate: disconnectSuiWallet } = useDisconnectWallet();
  const suiAddress = walletAccount?.address || null;

  // Modals and UI state
  const [openSuiModal, setOpenSuiModal] = useState(false);
  const [connectLoading, setConnectLoading] = useState(false);
  const [connectSuccess, setConnectSuccess] = useState({ status: "loading" });
  const [disconnect, setDisConnect] = useState(false);
  const [walletConnection, setWalletConnection] = useState(false);

  // Migration state
  const [migrationLoading, setMigrationLoading] = useState(false);
  const [migrationSuccess, setMigrationSuccess] = useState({ status: "loading" });
  const [userNFTs, setUserNFTs] = useState([]);
  const [migrationStep, setMigrationStep] = useState(1); // 1: Connect wallets, 2: Migrate
  const [loadingNFTs, setLoadingNFTs] = useState(false);
  const [totalNFTCount, setTotalNFTCount] = useState(0);


  useEffect(() => {
    window.addEventListener("ICONEX_RELAY_RESPONSE", eventHandler, false);
    return () => {
      window.removeEventListener("ICONEX_RELAY_RESPONSE", eventHandler);
    };
  }, []);

  const eventHandler = async (event) => {
    const response = await connectResponse(event);
    const success = { status: "success" };
    const cancel = { status: "cancel" };
    const error = { status: "error" };

    switch (response.type) {
      case "address":
        dispatch(fetchWalletSuccess(response.data));
        setConnectSuccess(success);
        break;

      case "json-RPC":
        getTxResultWithTimer(response.data.result);
        break;

      case "cancel":
        modalSuccessHandler(cancel);
        break;

      case "default":
        modalSuccessHandler(cancel);
        break;

      default:
        modalSuccessHandler(error);
    }
  };

  const getTxResultWithTimer = (txHash) => {
    setTimeout(() => {
      getTx(txHash);
    }, 4000);
  };

  const getTx = async (txHash) => {
    const res = await icon.getTransactionResult(txHash);
    if (res.status === "pending") {
      getTxResultWithTimer(txHash);
    } else {
      setTimeout(() => {
        modalSuccessHandler(res);
      }, 4000);
    }
  };

  const modalSuccessHandler = (res) => {
    if (res.status === "success") {
      setMigrationSuccess(res);
      if (address) {
        loadUserNFTs();
      }
    }
  };

  // ICON Connect
  const handleLogin = async () => {
    setConnectSuccess({ status: "loading" });
    setConnectLoading(true);
    icon.handleLogin();
  };

  // ICON Disconnect
  const handleDisConnected = () => {
    dispatch(removeWalletAddress());
  };


  // Load user NFTs when ICON wallet is connected
  useEffect(() => {
    if (address) {
      loadUserNFTs();
    }
  }, [address]);

  const loadUserNFTs = async () => {
    if (!address) return;
    
    setLoadingNFTs(true);
    try {
      let totalCount = 0;
      let allNFTIds = [];
      
      // Get first page to determine total pages
      const firstPage = await icon._getOwnedNft(address, `0x1`);
      console.log("First page response:", firstPage);
      
      // Handle both direct result and jsonrpc wrapper
      const firstPageResult = firstPage?.result || firstPage;
      console.log("First page result:", firstPageResult);
      
      if (firstPageResult && firstPageResult.nftList) {
        // Add NFTs from first page
        allNFTIds = [...firstPageResult.nftList];
        console.log("NFTs from first page:", firstPageResult.nftList);
        
        // Get total pages from the response
        const totalPages = parseInt(firstPageResult.totalPage, 16);
        console.log("Total pages:", totalPages);
        
        // Fetch remaining pages if there are more
        for (let page = 2; page <= totalPages; page++) {
          try {
            const pageData = await icon._getOwnedNft(address, `0x${page.toString(16)}`);
            console.log(`Page ${page} response:`, pageData);
            // Handle both direct result and jsonrpc wrapper
            const pageResult = pageData?.result || pageData;
            if (pageResult && pageResult.nftList) {
              allNFTIds = [...allNFTIds, ...pageResult.nftList];
              console.log(`NFTs from page ${page}:`, pageResult.nftList);
            }
          } catch (error) {
            console.error(`Error fetching page ${page}:`, error);
          }
        }
        
        totalCount = allNFTIds.length;
        console.log("Total NFT count:", totalCount);
        console.log("All NFT IDs:", allNFTIds);
        setTotalNFTCount(totalCount);
        
        // Store just the NFT IDs for migration
        setUserNFTs(allNFTIds.map(id => ({ id })));
      } else {
        console.log("No NFTs found or invalid response structure");
        setTotalNFTCount(0);
        setUserNFTs([]);
      }
    } catch (error) {
      console.error("Error loading NFTs:", error);
      setTotalNFTCount(0);
      setUserNFTs([]);
    } finally {
      setLoadingNFTs(false);
    }
  };

  // Handle migration
  const handleMigration = async () => {
    if (!address || !suiAddress || !Array.isArray(userNFTs) || userNFTs.length === 0) return;

    setMigrationLoading(true);
    setMigrationSuccess({ status: "loading" });

    try {
      const nftIds = userNFTs.map(nft => nft.id);
      const migrationPayload = {
        fromAddress: address,
        toAddress: suiAddress,
        nftIds: nftIds
      };

      // Call the actual migration function
      const res = await icon.migrateNFTs(
        migrationPayload,
        walletType,
        walletPath,
        transport
      );

      if (walletType === WALLET_TYPE.LEDGER) {
        if (res?.name === "TransportStatusError") {
          setMigrationSuccess({ status: "cancel" });
          setMigrationLoading(false);
        } else if (res) {
          getTxResultWithTimer(res);
        } else {
          setMigrationSuccess({ status: "error" });
          setMigrationLoading(false);
        }
      } else {
        // For ICONEX wallet, response will be handled by event listener
        // No need to manually set success/error here
      }
    } catch (error) {
      console.error("Migration error:", error);
      setMigrationSuccess({ status: "error", message: error.message });
      setMigrationLoading(false);
    }
  };

  const handleMigrationModalClose = () => {
    setMigrationLoading(false);
    setMigrationSuccess({ status: "loading" });

    navigate(WALLET.addSlug(WALLET.INDEX, address));
  };

  useEffect(() => {
    if (address && suiAddress) {
      setMigrationStep(2);
    }else{
      setMigrationStep(1);
    }
  }, [address, suiAddress]);

  return (
    <div className="container py-5">
      <div className="row justify-content-center">
        <div className="col-lg-8">
          <div className="card p-4">
            <div className="card-header text-center">
              {/* <h2 className="mb-0">NFT Migration Portal</h2> */}
              <p className="text-muted mb-0">Migrate your GangstaBet NFTs from ICON to SUI Network</p>
            </div>
            <div className="card-body">
              {/* Step 1: Connect Wallets */}
              {migrationStep === 1 && (
                <div className="migration-step">
                  <h4 className="mb-4 text-center">Step 1: Connect Both Wallets</h4>
                  
                  <div className="row">
                    <div className="col-md-6 mb-4">
                      <div className="wallet-connection-card">
                        <div className="d-flex align-items-center mb-3">
                          {/* <img src={WALLET_ICON.SOURCE} alt="ICON" className="wallet-icon me-2" /> */}
                          <h5 className="mb-0 m-auto">ICON Wallet</h5>
                        </div>
                        {address ? (
                          <div className="wallet-status connected">
                            {/* <div className="status-badge success">Connected</div> */}
                            <div className="address-text">{address.slice(0, 12)}...{address.slice(-4)}</div>
                            <button className="btn btn-outline-danger btn-sm mt-2" onClick={() => setDisConnect(true)}>
                              Disconnect
                            </button>
                          </div>
                        ) : (
                          <div className="wallet-status">
                            {/* <div className="status-badge">Not Connected</div> */}
                            <button className="btn btn-primary mt-2" onClick={() => setWalletConnection(true)}>
                              Connect ICON Wallet
                            </button>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="col-md-6 mb-4">
                      <div className="wallet-connection-card">
                        <div className="d-flex align-items-center mb-3">
                          {/* <img src={WALLET_ICON.SOURCE} alt="SUI" className="wallet-icon me-2" /> */}
                          <h5 className="mb-0 m-auto">SUI Wallet</h5>
                        </div>
                        {suiAddress ? (
                          <div className="wallet-status connected">
                            {/* <div className="status-badge success">Connected</div> */}
                            <div className="address-text">{suiAddress.slice(0, 12)}...{suiAddress.slice(-4)}</div>
                            <button className="btn btn-outline-danger btn-sm mt-2" onClick={() => disconnectSuiWallet()}>
                              Disconnect
                            </button>
                          </div>
                        ) : (
                          <div className="wallet-status">
                            {/* <div className="status-badge">Not Connected</div> */}
                            <ConnectModal
                              trigger={
                                <button className="btn btn-primary mt-2">
                                  Connect SUI Wallet
                                </button>
                              }
                              open={openSuiModal}
                              onOpenChange={setOpenSuiModal}
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {(address && suiAddress) && (
                    <div className="text-center mt-4">
                      <button 
                        className="btn btn-primary btn-lg"
                        onClick={() => setMigrationStep(2)}
                      >
                        <i className="fas fa-arrow-right me-2"></i>
                        Proceed to Migration
                      </button>
                    </div>
                  )}
                </div>
              )}

                            {/* Step 2: Migrate All NFTs */}
              {migrationStep === 2 && (
                <div className="migration-step">
                  <h4 className="mb-4 text-center">Step 2: Migrate All NFTs</h4>
                  
                  {Array.isArray(userNFTs) && userNFTs.length > 0 ? (
                    <>
                      <div className="text-center mb-4">
                        <div className="alert alert-info">
                          <i className="fas fa-info-circle me-2"></i>
                          <strong>{totalNFTCount}</strong> NFTs found in your ICON wallet
                        </div>
                      </div>

                      <div className="text-center">
                        <button 
                          className="btn btn-primary btn-lg"
                          onClick={handleMigration}
                          disabled={migrationLoading}
                        >
                          {migrationLoading ? (
                            <>
                              <span className="spinner-border spinner-border-sm me-2"></span>
                              Migrating...
                            </>
                          ) : (
                            `Migrate All ${totalNFTCount} NFT${totalNFTCount > 1 ? 's' : ''}`
                          )}
                        </button>
                      </div>
                    </>
                  ) : (
                    <div className="text-center py-5">
                      {loadingNFTs ? (
                        <div className="alert alert-info">
                          <div className="spinner-border spinner-border-sm me-2" role="status">
                            <span className="visually-hidden">Loading...</span>
                          </div>
                          Loading NFTs from your ICON wallet...
                        </div>
                      ) : (
                        <div className="alert alert-info">
                          <i className="fas fa-info-circle me-2"></i>
                          No NFTs found in your ICON wallet. Please make sure you have GangstaBet NFTs to migrate.
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}


            </div>
          </div>
        </div>
      </div>

      {/* Modals */}
      <ConnectWalletModal
        isVisible={connectLoading}
        response={connectSuccess}
        onClick={() => setConnectLoading(false)}
      />
      <DisconnectModal
        isVisible={disconnect}
        onCancel={() => setDisConnect(false)}
        onDisconnect={handleDisConnected}
      />
      <ConnectionOptionModal
        isVisible={walletConnection}
        onClose={() => setWalletConnection(false)}
        iconexConnection={handleLogin}
      />

      {/* Migration Modal */}
      <MigrationModal
        isVisible={migrationLoading}
        response={migrationSuccess}
        onClick={handleMigrationModalClose}
        nftCount={totalNFTCount}
      />
    </div>
  );
};

export default Migration;